import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = {
	hidden: { opacity: 0, y: 30 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const menuItems = [
	{ label: "Home", path: "/" },
	{ label: "About", path: "/about" },
	{ label: "Services", path: "/services" },
	{ label: "Projects", path: "/projects" },
	{ label: "Career", path: "/career" },
	{ label: "Vendors", path: "/vendors" },
	{ label: "Contact", path: "/contact" },
];

const Header = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 10);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<div className="relative">
			{/* Top bar */}
			<motion.header
				layout={false}
				className={`fixed top-0 left-0 w-full z-30 flex items-center justify-between px-6 md:px-16 py-5 transition-all duration-300 backdrop-blur-lg ${scrolled ? "bg-white/8 shadow-lg" : "bg-white/4"}`}
				style={{
					position: "fixed",
					WebkitBackdropFilter: "blur(16px)",
					backdropFilter: "blur(16px)",
				}}
				variants={fadeUp}
			>
				<Link to="/" className="relative">
				  <motion.div className="flex items-center gap-3" variants={fadeUp} whileHover={{ scale: 1.03 }}>
					  <motion.div
						  className="flex h-12 w-12 items-center justify-center rounded-full "
						  initial={{ rotate: -10 }}
						  animate={{ rotate: 0 }}
						  transition={{ duration: 0.5 }}
					  >
						  <img src="/images/logo.png" alt="Logo" className="h-18 w-18 object-contain" />
					  </motion.div>
					  <div className="leading-tight text-white">
						  <p className="text-sm font-semibold tracking-[0.25em] uppercase">
							  SKYLINE MEP
						  </p>
						  <p className="text-sm font-semibold tracking-[0.25em] uppercase">
							  CONSULTANTS
						  </p>
					  </div>
				  </motion.div>
				</Link>

				<motion.button
					type="button"
					className="flex flex-col items-end gap-1 text-xs tracking-[0.35em] uppercase z-30 relative"
					onClick={toggleMenu}
					variants={fadeUp}
					whileHover={{ x: -2 }}
					whileTap={{ scale: 0.95 }}
				>
					<motion.span 
						className="block h-0.5 w-7 bg-white rounded"
						animate={{
							rotate: isMenuOpen ? 45 : 0,
							y: isMenuOpen ? 2 : 0,
							width: isMenuOpen ? '2.5rem' : '1.75rem',
						}}
						transition={{ duration: 0.3 }}
					/>
					<motion.span 
						className="block h-0.5 w-5 bg-white rounded"
						animate={{
							opacity: isMenuOpen ? 0 : 1,
							scaleX: isMenuOpen ? 0 : 1,
						}}
						transition={{ duration: 0.2 }}
					/>
					<motion.span 
						className="block h-0.5 w-7 bg-white rounded"
						animate={{
							rotate: isMenuOpen ? -45 : 0,
							y: isMenuOpen ? -2 : 0,
							width: isMenuOpen ? '2.5rem' : '1.75rem',
						}}
						transition={{ duration: 0.3 }}
					/>
					<motion.span 
						className="mt-1 text-white"
						animate={{ opacity: isMenuOpen ? 0 : 1 }}
					>
						Menu
					</motion.span>
				</motion.button>
			</motion.header>

			{/* Menu Overlay - All screen sizes */}
			<AnimatePresence>
				{isMenuOpen && (
					<motion.div
						className="fixed inset-0 z-30 bg-slate-900/95 backdrop-blur-md"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						onClick={toggleMenu}
					/>
				)}
			</AnimatePresence>

			{/* Navigation Menu - Visible on ALL screen sizes */}
			<AnimatePresence>
				{isMenuOpen && (
					<motion.ul
						className="fixed top-0 right-0 h-full w-72 z-40 flex flex-col justify-center py-12 px-8 bg-slate-900/98 backdrop-blur-2xl shadow-2xl border-l-2 border-sky-500/50"
						initial={{ x: '100%' }}
						animate={{ x: 0 }}
						exit={{ x: '100%' }}
						transition={{ type: "spring", stiffness: 300, damping: 30 }}
					>
						{menuItems.map((item, index) => (
							<motion.li
								key={item.label}
								className="mb-6 last:mb-0"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: index * 0.06 }}
								whileHover={{ x: 8, scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
							>
								<Link
									to={item.path}
									className="block py-4 px-6 text-xl font-bold tracking-wider uppercase text-white hover:text-sky-400 hover:bg-white/10 rounded-xl transition-all duration-200 border-l-4 border-transparent hover:border-sky-400 shadow-lg hover:shadow-sky-500/25"
									onClick={toggleMenu}
								>
									{item.label}
								</Link>
							</motion.li>
						))}
					</motion.ul>
				)}
			</AnimatePresence>
		</div>
	);
};

export default Header;
